import React, { useState, useEffect } from 'react';
import {
  Search,
  Plus,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  Edit,
  Trash2,
  Loader2,
  X,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  createUnlistedFirm,
  getAllUnlistedFirms,
  deleteUnlistedFirm,
  updateUnlistedFirm,
} from '@/services/unlistedFirmsApi';
import { getDropDownFirms } from '@/services/newsApi';
// import { getAllFirms } from '@/services/firmApi'; // adjust if your firms API name is different

export const UnlistedFirmsPage = () => {
  const [firms, setFirms] = useState([]);
  const [allFirms, setAllFirms] = useState([]); // for dropdown
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFirm, setSelectedFirm] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  // Form state
  const [firmId, setFirmId] = useState('');
  const [status, setStatus] = useState('Unlisted');
  const [reason, setReason] = useState('');
  const [sourceLinks, setSourceLinks] = useState(['']);

  // ==================== FETCH ====================
  const fetchUnlistedFirms = async () => {
    try {
      setLoading(true);
      const data = await getAllUnlistedFirms();
      setFirms(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load unlisted firms');
    } finally {
      setLoading(false);
    }
  };

 const fetchAllFirms = async () => {
  try {
    const data = await getDropDownFirms();
    setAllFirms(Array.isArray(data) ? data : data?.data || []);
  } catch (error) {
    console.error(error);
    toast.error('Failed to load firms for dropdown');
  }
};

  useEffect(() => {
    fetchUnlistedFirms();
    fetchAllFirms();
  }, []);

  // ==================== FORM HELPERS ====================
  const resetForm = () => {
    setFirmId('');
    setStatus('Unlisted');
    setReason('');
    setSourceLinks(['']);
  };

  const openCreate = () => {
    resetForm();
    setIsAddingNew(true);
    setSelectedFirm(null);
  };

  const openEdit = (item) => {
    setSelectedFirm(item);
    setIsAddingNew(false);

    setFirmId(item.firm_id || item.firmId || '');
    setStatus(item.status || 'Unlisted');
    setReason(item.reason || item.reasonSummary || '');
    setSourceLinks(
      item.source_links?.length > 0
        ? item.source_links
        : item.sourceLinks?.length > 0
        ? item.sourceLinks
        : ['']
    );
  };

  const closeSheet = () => {
    setIsAddingNew(false);
    setSelectedFirm(null);
    resetForm();
  };

  // Source links helpers
  const addSourceLink = () => {
    setSourceLinks([...sourceLinks, '']);
  };

  const updateSourceLink = (index, value) => {
    const updated = [...sourceLinks];
    updated[index] = value;
    setSourceLinks(updated);
  };

  const removeSourceLink = (index) => {
    if (sourceLinks.length === 1) return;
    setSourceLinks(sourceLinks.filter((_, i) => i !== index));
  };

  // ==================== BUILD PAYLOAD ====================
  const buildPayload = (includeId = false) => {
  const payload = {
    firm_id: firmId,
    status,
    reason,
    source_links: sourceLinks.filter((link) => link.trim() !== ''),
  };

  if (includeId && selectedFirm?.id) {
    payload.id = selectedFirm.id;
  }

  return payload;
};

const handleSave = async () => {
  if (!firmId) {
    toast.error('Please select a firm');
    return;
  }
  if (!reason.trim()) {
    toast.error('Reason is required');
    return;
  }

  setSaving(true);

  try {
    if (isAddingNew) {
      await createUnlistedFirm(buildPayload(false));
      toast.success('Unlisted firm created successfully');
    } else {
      // payload: { id, firm_id, status, reason, source_links }
      await updateUnlistedFirm(buildPayload(true));
      toast.success('Unlisted firm updated successfully');
    }

    closeSheet();
    fetchUnlistedFirms();
  } catch (error) {
    console.error(error);
    toast.error(error?.response?.data?.message || 'Failed to save');
  } finally {
    setSaving(false);
  }
};

  // ==================== DELETE ====================
  const handleDelete = async () => {
    if (!deleteItem) return;

    try {
      await deleteUnlistedFirm(deleteItem.id);
      toast.success('Unlisted firm deleted');
      setDeleteItem(null);
      fetchUnlistedFirms();
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete');
    }
  };

  // ==================== FILTER & COUNTS ====================
  const filteredFirms = firms.filter((firm) => {
    const firmData = allFirms.find((f) => f.id === (firm.firm_id || firm.firmId));
    const name = firmData?.name || firm.name || '';
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const unlistedCount = firms.filter((f) => f.status === 'Unlisted').length;
  const warningCount = firms.filter((f) => f.status === 'Warning').length;
  const delistedCount = firms.filter((f) => f.status === 'Delisted').length;

  return (
    <div>
      <PageHeader
        title="Unlisted Firms"
        description="Manage firms with warnings, delistings, or under review"
        breadcrumbs={['Admin', 'Content', 'Unlisted Firms']}
        actions={
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Add Unlisted Firm
          </Button>
        }
      />

      {/* Stats Cards */}
      <div className="grid gap-6 md:grid-cols-3 mb-6">
        <Card className="rounded-xl border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                  Unlisted
                </p>
                <p className="text-3xl font-bold tracking-tight text-slate-900 mt-2">
                  {unlistedCount}
                </p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <AlertTriangle className="h-6 w-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                  Warning
                </p>
                <p className="text-3xl font-bold tracking-tight text-slate-900 mt-2">
                  {warningCount}
                </p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                  Delisted
                </p>
                <p className="text-3xl font-bold tracking-tight text-slate-900 mt-2">
                  {delistedCount}
                </p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search unlisted firms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Firm
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Status
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Reason
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Date Flagged
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Community Votes
                    </TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredFirms.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                        No unlisted firms found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredFirms.map((firm) => {
                      const firmData = allFirms.find(
                        (f) => f.id === (firm.firm_id || firm.firmId)
                      );
                      return (
                        <TableRow key={firm.id} className="hover:bg-slate-50">
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {firmData?.logo && (
                                <img
                                  src={firmData.logo}
                                  alt=""
                                  className="h-10 w-10 rounded"
                                />
                              )}
                              <span className="text-sm font-medium text-slate-900">
                                {firmData?.name || firm.name || 'Unknown'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={firm.status} />
                          </TableCell>
                          <TableCell>
                            <p className="text-sm text-slate-600 line-clamp-2 max-w-md">
                              {firm.reason || firm.reasonSummary}
                            </p>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-600">
                              {firm.date_flagged || firm.dateFlagged
                                ? format(
                                    new Date(firm.date_flagged || firm.dateFlagged),
                                    'MMM dd, yyyy'
                                  )
                                : '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1">
                                <ThumbsUp className="h-3 w-3 text-emerald-600" />
                                <span className="text-sm font-medium">
                                  {firm.community_votes?.trust ??
                                    firm.communityVotes?.trust ??
                                    0}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <ThumbsDown className="h-3 w-3 text-red-600" />
                                <span className="text-sm font-medium">
                                  {firm.community_votes?.distrust ??
                                    firm.communityVotes?.distrust ??
                                    0}
                                </span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEdit(firm)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-600"
                                onClick={() => setDeleteItem(firm)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ==================== SHEET ==================== */}
      <Sheet
        open={isAddingNew || !!selectedFirm}
        onOpenChange={(open) => {
          if (!open) closeSheet();
        }}
      >
        <SheetContent className="w-[500px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {isAddingNew ? 'Add Unlisted Firm' : 'Edit Unlisted Firm'}
            </SheetTitle>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Firm Select */}
            <div>
              <Label>Firm</Label>
              <Select value={firmId} onValueChange={setFirmId}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select firm" />
                </SelectTrigger>
                <SelectContent>
                  {allFirms.map((firm) => (
                    <SelectItem key={firm.id} value={firm.id}>
                      {firm.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div>
              <Label>Status</Label>
              <RadioGroup
                value={status}
                onValueChange={setStatus}
                className="mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Unlisted" id="unlisted" />
                  <Label htmlFor="unlisted">Unlisted</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Warning" id="warning" />
                  <Label htmlFor="warning">Warning</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Delisted" id="delisted" />
                  <Label htmlFor="delisted">Delisted</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Reason */}
            <div>
              <Label>Reason / Summary</Label>
              <Textarea
                placeholder="Describe the reason for flagging this firm..."
                className="mt-2"
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>

            {/* Source Links */}
            <div>
              <Label>Source Links</Label>
              <div className="mt-2 space-y-2">
                {sourceLinks.map((link, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      placeholder="https://..."
                      value={link}
                      onChange={(e) => updateSourceLink(index, e.target.value)}
                    />
                    {sourceLinks.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeSourceLink(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={addSourceLink}
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Link
              </Button>
            </div>

            <Button className="w-full" onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isAddingNew ? 'Create Unlisted Firm' : 'Update Unlisted Firm'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteItem}
        onOpenChange={() => setDeleteItem(null)}
        title="Delete Unlisted Firm"
        description="Are you sure you want to delete this entry? This action cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
      />
    </div>
  );
};